import { LitElement, html } from 'lit';
import { ContextConsumer } from '@lit/context';
import { authContext } from '../services/context/auth-context.js';

class QuoteLandingPage extends LitElement {
    static properties = {
        quoteReference: { type: String },
        error: { type: String },
        authState: { type: Object, state: true },
    };

    // Use Light DOM for Tailwind compatibility
    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        this.quoteReference = '';
        this.error = null;
        this.authState = null;
    }

    connectedCallback() {
        super.connectedCallback();
        // Subscribe to auth context using functional API
        this._authConsumer = new ContextConsumer(this, {
            context: authContext,
            subscribe: true,
            callback: (value) => {
                this.authState = value;
            },
        });

        /**
         * Read quote reference from URL query parameter
         * Used when user is redirected back from error page
         */
        const params = new URLSearchParams(window.location.hash.split('?')[1]);
        const refParam = params.get('ref');

        if (refParam) {
            this.quoteReference = refParam;
            // Clean URL after reading parameter
            window.history.replaceState(null, '', '#/');
        }
    }

    handleReferenceInput(e) {
        this.quoteReference = e.target.value.toUpperCase().slice(0, 32);
        // Clear error when user starts typing
        if (this.error) {
            this.error = null;
        }
    }

    handleSubmit(e) {
        e.preventDefault();

        const ref = this.quoteReference.trim();

        if (!ref) {
            this.error = 'Please enter a quote reference';
            return;
        }

        // TODO: Add quote reference validation (see TODO.md #1)
        // For now, just navigate to the quote details page
        window.location.hash = `/quote/${ref}`;
    }

    get userGreeting() {
        if (this.authState?.user?.email) {
            return `Welcome, ${this.authState.user.email}`;
        }
        return 'Welcome';
    }

    render() {
        return html`
            <div class="flex flex-col items-center justify-center min-h-[60vh] px-4">
                <div class="w-full max-w-md pt-4">
                    <h1 class="text-3xl font-bold mb-2 text-center">${this.userGreeting}</h1>
                    <p class="text-center text-gray-600 mb-8">
                        Enter your quote reference to view your quote details
                    </p>

                    <form @submit="${this.handleSubmit}" class="space-y-4">
                        <div class="form-control">
                            <label for="quote-reference-input" class="label">
                                <span class="label-text">Quote Reference</span>
                            </label>
                            <input
                                id="quote-reference-input"
                                type="text"
                                .value="${this.quoteReference}"
                                @input="${this.handleReferenceInput}"
                                maxlength="32"
                                placeholder="XXXX-XXXX-XXXX"
                                class="input input-bordered w-full ${this.error
                                    ? 'input-error'
                                    : ''}"
                                required
                            />
                            ${this.error
                                ? html`
                                      <div class="label">
                                          <span class="label-text-alt text-error">
                                              ${this.error}
                                          </span>
                                      </div>
                                  `
                                : ''}
                            <div class="label">
                                <span class="label-text-alt text-gray-500">
                                    Your quote reference can be found in the email we sent you
                                </span>
                            </div>
                        </div>

                        <button type="submit" class="btn btn-primary w-full">View Quote</button>
                    </form>

                    <div class="mt-8 text-center">
                        <p class="text-sm text-gray-500 mb-2">
                            Need help finding your quote reference?
                        </p>
                        <a
                            href="mailto:info@reach.nz"
                            class="text-peppermint hover:underline text-sm"
                        >
                            Contact Support
                        </a>
                    </div>
                </div>
            </div>
        `;
    }
}

customElements.define('quote-landing-page', QuoteLandingPage);
